import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChannelPicker } from '../ChannelPicker';
import { ChannelFilters } from '../../../domain';
import { MAX_CRAWLER_CHANNELS_PER_ASSIGN } from '../../../domain/entities/CrawlerChannel';
import { useChannelPickerSource } from '../../hooks/useChannelPickerSource';
import { useChannelCategories } from '../../hooks/useChannelCategories';

interface AssignCrawlerChannelsModalProps {
  open: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (channelUuids: string[]) => Promise<void>;
}

const PAGE_SIZE = 10;

const AssignCrawlerChannelsModal: React.FC<AssignCrawlerChannelsModalProps> = ({ open, isSaving, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { channels, pagination, filters, isLoading, error, load, reset, countChannels } = useChannelPickerSource(PAGE_SIZE);
  const { categories } = useChannelCategories({ loadOnMount: open });
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    setSelected({});
    reset();
  }, [open, reset]);

  const selectedUuids = Object.keys(selected).filter((uuid) => selected[uuid]);

  const handleSelectionChange = (next: Record<string, boolean>) => {
    const nextCount = Object.values(next).filter(Boolean).length;
    if (nextCount > MAX_CRAWLER_CHANNELS_PER_ASSIGN) {
      message.warning(t('crawler-channels-too-many', { ns: 'errors' }));
      return;
    }
    setSelected(next);
  };

  const handleFiltersChange = useCallback((next: ChannelFilters) => {
    load(1, pagination.perPage, next);
  }, [load, pagination.perPage]);

  const handleLoadMore = useCallback(() => {
    load(pagination.page + 1, pagination.perPage, filters, true);
  }, [load, pagination.page, pagination.perPage, filters]);

  return (
    <Modal
      open={open}
      width={880}
      title={t('crawler-assign-channels', { ns: 'titles' })}
      okText={t('crawler-assign-channels-submit', { ns: 'buttons', count: selectedUuids.length })}
      cancelText={t('cancel', { ns: 'buttons' })}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: selectedUuids.length === 0 }}
      onCancel={onCancel}
      onOk={() => onSubmit(selectedUuids)}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">
        {t('crawler-assign-channels-hint', { ns: 'standard', count: MAX_CRAWLER_CHANNELS_PER_ASSIGN })}
      </Typography.Paragraph>
      {selectedUuids.length >= MAX_CRAWLER_CHANNELS_PER_ASSIGN && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('crawler-assign-channels-limit-reached', { ns: 'standard', count: MAX_CRAWLER_CHANNELS_PER_ASSIGN })}
        />
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t(error, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) })}
        />
      )}
      <ChannelPicker
        channels={channels}
        categories={categories}
        isLoading={isLoading}
        pagination={pagination}
        filters={filters}
        selectedChannels={selected}
        mode="multiple"
        onFiltersChange={handleFiltersChange}
        onSelectionChange={handleSelectionChange}
        onPageChange={(page, pageSize) => load(page, pageSize, filters)}
        onLoadMore={handleLoadMore}
        onPreviewCount={countChannels}
      />
    </Modal>
  );
};

export default AssignCrawlerChannelsModal;
